import { useCallback, useEffect, useRef, useState } from "react";
import {
  CLICK_DISTANCE_PX,
  CLICK_DURATION_MS,
  DEFAULT_HEIGHT,
  DEFAULT_WIDTH,
  PADDING,
  SNAP_THRESHOLD,
} from "../constants";
import type { Position } from "../types";

// Custom hook for draggable button logic
export const useDraggableButton = () => {
  const [position, setPosition] = useState<Position>({
    x: window.innerWidth - DEFAULT_WIDTH + PADDING,
    y: window.innerHeight - DEFAULT_HEIGHT + PADDING,
  });
  const [isDragging, setIsDragging] = useState(false);
  const dragOffset = useRef<Position>({ x: 0, y: 0 });
  const downTime = useRef(0);
  const startPos = useRef<Position>({ x: 0, y: 0 });
  const buttonRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number | null>(null);

  // Snap to edge function
  const snapToEdge = useCallback((x: number, y: number): Position => {
    const buttonWidth = buttonRef.current?.offsetWidth ?? DEFAULT_WIDTH;
    const buttonHeight = buttonRef.current?.offsetHeight ?? DEFAULT_HEIGHT;

    const distToLeft = x;
    const distToRight = window.innerWidth - (x + buttonWidth);
    const distToTop = y;
    const distToBottom = window.innerHeight - (y + buttonHeight);

    let snappedX = x;
    let snappedY = y;

    if (distToLeft <= SNAP_THRESHOLD || distToRight <= SNAP_THRESHOLD) {
      snappedX = distToLeft < distToRight ? PADDING : window.innerWidth - buttonWidth - PADDING;
    }

    if (distToTop <= SNAP_THRESHOLD || distToBottom <= SNAP_THRESHOLD) {
      snappedY = distToTop < distToBottom ? PADDING : window.innerHeight - buttonHeight - PADDING;
    }

    return { x: snappedX, y: snappedY };
  }, []);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      if (!isDragging) {
        setPosition({
          x: window.innerWidth - DEFAULT_WIDTH - PADDING,
          y: window.innerHeight - DEFAULT_HEIGHT - PADDING,
        });
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isDragging]);

  // Extract and memoize handlePointerUp
  const handlePointerUp = useCallback(() => {
    if (isDragging) {
      const dragDuration = Date.now() - downTime.current;
      const dx = position.x - startPos.current.x;
      const dy = position.y - startPos.current.y;
      const totalDistance = Math.sqrt(dx * dx + dy * dy);
      const isClick = dragDuration < CLICK_DURATION_MS && totalDistance < CLICK_DISTANCE_PX;
      setIsDragging(false);
      document.body.style.userSelect = "";
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      if (!isClick) {
        const snapped = snapToEdge(position.x, position.y);
        setPosition(snapped);
      }
      return isClick;
    }
    return false;
  }, [isDragging, position.x, position.y, snapToEdge]);

  // Modify the dragging useEffect to use the extracted handlePointerUp
  useEffect(() => {
    const handlePointerMove = (e: PointerEvent) => {
      if (isDragging && buttonRef.current) {
        const buttonWidth = buttonRef.current.offsetWidth;
        const buttonHeight = buttonRef.current.offsetHeight;
        const newX = Math.max(
          PADDING,
          Math.min(window.innerWidth - buttonWidth - PADDING, e.clientX - dragOffset.current.x),
        );
        const newY = Math.max(
          PADDING,
          Math.min(window.innerHeight - buttonHeight - PADDING, e.clientY - dragOffset.current.y),
        );
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
        rafRef.current = requestAnimationFrame(() => setPosition({ x: newX, y: newY }));
      }
    };

    if (isDragging) {
      document.addEventListener("pointermove", handlePointerMove, { passive: true });
      document.addEventListener("pointerup", handlePointerUp);
      document.addEventListener("pointercancel", handlePointerUp);
      document.body.style.userSelect = "none";
    }

    return () => {
      document.removeEventListener("pointermove", handlePointerMove);
      document.removeEventListener("pointerup", handlePointerUp);
      document.removeEventListener("pointercancel", handlePointerUp);
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [isDragging, handlePointerUp]);

  // Handle pointer down
  const handlePointerDown = (e: React.PointerEvent) => {
    if (buttonRef.current) {
      buttonRef.current.setPointerCapture(e.pointerId);
      const rect = buttonRef.current.getBoundingClientRect();
      dragOffset.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
      startPos.current = { x: rect.left, y: rect.top };
      downTime.current = Date.now();
      setIsDragging(true);
      e.preventDefault();
    }
  };

  return { position, isDragging, buttonRef, handlePointerDown, handlePointerUp };
};
